import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface ContratoData {
	tipoAlquiler: string
	fechaInicio: string
	fechaFin: string
	inmuebleDireccion: string
	inmuebleLocalidad: string
	inmuebleProvincia: string
	valorRenta: string
	alquileresImpagos: string
	sustitucionGarantia: string
	expensasServicios: string
}

interface CaucionAlquilerContratoScreenProps {
	onNext: (data: ContratoData) => void
	onBack: () => void
	data?: Partial<ContratoData>
}

const TIPO_ALQUILER_OPTIONS = [
	{ value: 'vivienda', label: 'Vivienda' },
	{ value: 'comercial', label: 'Comercial' },
	{ value: 'oficina', label: 'Oficina' },
	{ value: 'deposito', label: 'Depósito/Galpón' },
	{ value: 'otro', label: 'Otro' },
]

const PROVINCIAS = [
	'Buenos Aires',
	'CABA',
	'Catamarca',
	'Chaco',
	'Chubut',
	'Córdoba',
	'Corrientes',
	'Entre Ríos',
	'Formosa',
	'Jujuy',
	'La Pampa',
	'La Rioja',
	'Mendoza',
	'Misiones',
	'Neuquén',
	'Río Negro',
	'Salta',
	'San Juan',
	'San Luis',
	'Santa Cruz',
	'Santa Fe',
	'Santiago del Estero',
	'Tierra del Fuego',
	'Tucumán',
]

export function CaucionAlquilerContratoScreen({
	onNext,
	onBack,
	data,
}: CaucionAlquilerContratoScreenProps) {
	const [formData, setFormData] = useState<ContratoData>({
		tipoAlquiler: data?.tipoAlquiler || '',
		fechaInicio: data?.fechaInicio || '',
		fechaFin: data?.fechaFin || '',
		inmuebleDireccion: data?.inmuebleDireccion || '',
		inmuebleLocalidad: data?.inmuebleLocalidad || '',
		inmuebleProvincia: data?.inmuebleProvincia || '',
		valorRenta: data?.valorRenta || '',
		alquileresImpagos: data?.alquileresImpagos || '',
		sustitucionGarantia: data?.sustitucionGarantia || '',
		expensasServicios: data?.expensasServicios || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const formatCurrency = (value: string) => {
		const cleaned = value.replace(/\D/g, '')
		if (!cleaned) return ''
		return new Intl.NumberFormat('es-AR').format(parseInt(cleaned, 10))
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.tipoAlquiler) {
			newErrors.tipoAlquiler = 'Selecciona el tipo de alquiler'
		}
		if (!formData.fechaInicio) {
			newErrors.fechaInicio = 'Ingresa la fecha de inicio'
		}
		if (!formData.fechaFin) {
			newErrors.fechaFin = 'Ingresa la fecha de finalización'
		}
		if (!formData.inmuebleDireccion.trim()) {
			newErrors.inmuebleDireccion = 'Ingresa la dirección del inmueble'
		}
		if (!formData.inmuebleLocalidad.trim()) {
			newErrors.inmuebleLocalidad = 'Ingresa la localidad'
		}
		if (!formData.inmuebleProvincia) {
			newErrors.inmuebleProvincia = 'Selecciona la provincia'
		}
		if (!formData.valorRenta.trim()) {
			newErrors.valorRenta = 'Ingresa el valor de la renta'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.tipoAlquiler &&
		formData.fechaInicio &&
		formData.fechaFin &&
		formData.inmuebleDireccion.trim() &&
		formData.inmuebleLocalidad.trim() &&
		formData.inmuebleProvincia &&
		formData.valorRenta.trim()

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos del Contrato
					</h2>

					<div className="flex w-full flex-col gap-4">
						<div className="flex flex-col gap-1">
							<label className="text-sm font-medium text-neutral-700">
								Tipo de Alquiler <span className="text-red-500">*</span>
							</label>
							<select
								className={`w-full rounded-xl border px-4 py-3 text-base ${
									errors.tipoAlquiler ? 'border-red-500' : 'border-neutral-300'
								}`}
								value={formData.tipoAlquiler}
								onChange={(e) => {
									setFormData({ ...formData, tipoAlquiler: e.target.value })
									setErrors({ ...errors, tipoAlquiler: '' })
								}}
							>
								<option value="">Selecciona el tipo</option>
								{TIPO_ALQUILER_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</select>
							{errors.tipoAlquiler && (
								<span className="text-sm text-red-500">
									{errors.tipoAlquiler}
								</span>
							)}
						</div>

						<div className="grid grid-cols-2 gap-4">
							<InputText
								label="Fecha Inicio"
								labelPosition="internal"
								size="l"
								type="date"
								value={formData.fechaInicio}
								onChange={(e) => {
									setFormData({ ...formData, fechaInicio: e.target.value })
									setErrors({ ...errors, fechaInicio: '' })
								}}
								errorText={errors.fechaInicio}
								required
							/>
							<InputText
								label="Fecha Fin"
								labelPosition="internal"
								size="l"
								type="date"
								value={formData.fechaFin}
								onChange={(e) => {
									setFormData({ ...formData, fechaFin: e.target.value })
									setErrors({ ...errors, fechaFin: '' })
								}}
								errorText={errors.fechaFin}
								required
							/>
						</div>

						<InputText
							label="Dirección del Inmueble"
							labelPosition="internal"
							size="l"
							placeholder="Calle, número, piso, depto"
							value={formData.inmuebleDireccion}
							onChange={(e) => {
								setFormData({ ...formData, inmuebleDireccion: e.target.value })
								setErrors({ ...errors, inmuebleDireccion: '' })
							}}
							errorText={errors.inmuebleDireccion}
							required
						/>

						<div className="grid grid-cols-2 gap-4">
							<InputText
								label="Localidad"
								labelPosition="internal"
								size="l"
								placeholder="Localidad"
								value={formData.inmuebleLocalidad}
								onChange={(e) => {
									setFormData({
										...formData,
										inmuebleLocalidad: e.target.value,
									})
									setErrors({ ...errors, inmuebleLocalidad: '' })
								}}
								errorText={errors.inmuebleLocalidad}
								required
							/>
							<div className="flex flex-col gap-1">
								<label className="text-sm font-medium text-neutral-700">
									Provincia <span className="text-red-500">*</span>
								</label>
								<select
									className={`w-full rounded-xl border px-4 py-3 text-base ${
										errors.inmuebleProvincia
											? 'border-red-500'
											: 'border-neutral-300'
									}`}
									value={formData.inmuebleProvincia}
									onChange={(e) => {
										setFormData({
											...formData,
											inmuebleProvincia: e.target.value,
										})
										setErrors({ ...errors, inmuebleProvincia: '' })
									}}
								>
									<option value="">Selecciona</option>
									{PROVINCIAS.map((prov) => (
										<option key={prov} value={prov}>
											{prov}
										</option>
									))}
								</select>
								{errors.inmuebleProvincia && (
									<span className="text-sm text-red-500">
										{errors.inmuebleProvincia}
									</span>
								)}
							</div>
						</div>

						<InputText
							label="Valor de la Renta Mensual ($)"
							labelPosition="internal"
							size="l"
							placeholder="Valor del alquiler"
							value={formData.valorRenta}
							onChange={(e) => {
								setFormData({
									...formData,
									valorRenta: formatCurrency(e.target.value),
								})
								setErrors({ ...errors, valorRenta: '' })
							}}
							errorText={errors.valorRenta}
							required
						/>

						<div className="mt-4 rounded-lg bg-blue-50 p-4">
							<h3 className="font-parkinsans mb-3 text-base font-medium text-blue-800">
								Obligaciones a Garantizar (Opcionales)
							</h3>
							<div className="flex flex-col gap-3">
								<InputText
									label="Alquileres Impagos ($)"
									labelPosition="internal"
									size="l"
									placeholder="Monto a cubrir"
									value={formData.alquileresImpagos}
									onChange={(e) => {
										setFormData({
											...formData,
											alquileresImpagos: formatCurrency(e.target.value),
										})
									}}
								/>
								<InputText
									label="Sustitución de Garantía ($)"
									labelPosition="internal"
									size="l"
									placeholder="Monto a cubrir"
									value={formData.sustitucionGarantia}
									onChange={(e) => {
										setFormData({
											...formData,
											sustitucionGarantia: formatCurrency(e.target.value),
										})
									}}
								/>
								<InputText
									label="Expensas/Impuestos/Servicios ($)"
									labelPosition="internal"
									size="l"
									placeholder="Monto a cubrir"
									value={formData.expensasServicios}
									onChange={(e) => {
										setFormData({
											...formData,
											expensasServicios: formatCurrency(e.target.value),
										})
									}}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
